import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { formatIDR, formatDate } from '../../lib/format';
import AdminLayout from '../../components/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { StatusBadge } from '../../components/StatusBadge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';

export default function AdminCommissions() {
  const [data, setData] = useState(null);
  const [level, setLevel] = useState('0');
  const [status, setStatus] = useState('');
  const [sel, setSel] = useState({});
  const [payDlg, setPayDlg] = useState(false);
  const [pay, setPay] = useState({ payment_reference: '', payment_method: 'Bank Transfer', note: '' });

  const load = () => { const p = new URLSearchParams(); if (level!=='0') p.set('level',level); if (status) p.set('status',status);
    api.get(`/admin/commissions?${p}`).then(r => setData(r.data)).catch(()=>{}); };
  useEffect(() => { load(); }, [level, status]);

  const selectedIds = Object.keys(sel).filter(k => sel[k]);
  const selectedTotal = (data?.commissions||[]).filter(c => sel[c.commission_id]).reduce((a,c)=>a+c.amount,0);

  const bulk = async (action, extra={}) => {
    if (selectedIds.length === 0) return toast.error('Pilih komisi terlebih dahulu');
    try { const { data: res } = await api.post('/admin/commissions/bulk', { commission_ids: selectedIds, action, ...extra });
      toast.success(`${res.processed} komisi diproses`); setSel({}); setPayDlg(false); load();
    } catch (e) { toast.error(e.response?.data?.detail||'Gagal'); }
  };

  if (!data) return <AdminLayout title="Commissions"><div className="h-64 flex items-center justify-center text-muted-foreground">Memuat...</div></AdminLayout>;

  return (
    <AdminLayout title="Commission Management">
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 mb-6">
        {[['Total','TOTAL'],['Pending','PENDING'],['Approved','APPROVED'],['Available','AVAILABLE'],['Held','HELD'],['Paid','PAID']].map(([l,k]) => (
          <Card key={k} className="p-4"><div className="text-xs text-muted-foreground">{l}</div><div className="font-display text-lg font-bold tabular-nums">{formatIDR(data.summary[k]||0)}</div></Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Select value={level} onValueChange={setLevel}><SelectTrigger className="w-32" data-testid="filter-level"><SelectValue placeholder="Level" /></SelectTrigger><SelectContent><SelectItem value="0">Semua Level</SelectItem>{[1,2,3,4].map(l=><SelectItem key={l} value={String(l)}>Level {l}</SelectItem>)}</SelectContent></Select>
        <Select value={status||'ALL'} onValueChange={(v)=>setStatus(v==='ALL'?'':v)}><SelectTrigger className="w-40" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="ALL">Semua Status</SelectItem>{['PENDING','APPROVED','AVAILABLE','HELD','PAID','REJECTED','REVERSED'].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <div className="flex-1" />
        {selectedIds.length>0 && <>
          <span className="text-sm text-muted-foreground">{selectedIds.length} dipilih · {formatIDR(selectedTotal)}</span>
          <Button size="sm" variant="outline" onClick={()=>bulk('approve')}>Approve</Button>
          <Button size="sm" variant="outline" onClick={()=>bulk('hold')}>Hold</Button>
          <Button size="sm" variant="outline" onClick={()=>bulk('reject')}>Reject</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={()=>setPayDlg(true)} data-testid="process-payout-button">Process Payout</Button>
        </>}
      </div>

      <Card className="p-0 overflow-hidden"><div className="overflow-x-auto">
        <Table><TableHeader><TableRow><TableHead className="w-8"></TableHead><TableHead>Referrer</TableHead><TableHead>Referred</TableHead><TableHead>Level</TableHead><TableHead className="text-right">Jumlah</TableHead><TableHead>Periode</TableHead><TableHead>Rule</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.commissions.length===0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Tidak ada komisi</TableCell></TableRow>}
            {data.commissions.map(c => (
              <TableRow key={c.commission_id}>
                <TableCell>{['PENDING','APPROVED','AVAILABLE'].includes(c.status) && <Checkbox checked={!!sel[c.commission_id]} onCheckedChange={(v)=>setSel({...sel,[c.commission_id]:v})} data-testid={`sel-${c.commission_id}`} />}</TableCell>
                <TableCell className="font-medium">{c.referrer_name}</TableCell>
                <TableCell className="text-xs">{c.referred_name}</TableCell>
                <TableCell>L{c.level}</TableCell>
                <TableCell className="text-right tabular-nums">{formatIDR(c.amount)}</TableCell>
                <TableCell>{c.period}</TableCell>
                <TableCell className="text-xs text-muted-foreground">v{c.rule_version ?? '-'}</TableCell>
                <TableCell><StatusBadge status={c.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div></Card>

      <Dialog open={payDlg} onOpenChange={setPayDlg}>
        <DialogContent><DialogHeader><DialogTitle>Process Payout ({selectedIds.length} komisi · {formatIDR(selectedTotal)})</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Payment Reference</Label><Input value={pay.payment_reference} onChange={e=>setPay({...pay,payment_reference:e.target.value})} className="mt-1.5" data-testid="payout-reference" /></div>
            <div><Label>Payment Method</Label><Input value={pay.payment_method} onChange={e=>setPay({...pay,payment_method:e.target.value})} className="mt-1.5" /></div>
            <div><Label>Catatan Admin</Label><Input value={pay.note} onChange={e=>setPay({...pay,note:e.target.value})} className="mt-1.5" /></div>
            <p className="text-xs text-muted-foreground">Setiap komisi akan ditandai PAID + ledger entry dibuat (idempoten, tidak akan double).</p>
          </div>
          <DialogFooter><Button onClick={()=>bulk('mark_paid', pay)} data-testid="confirm-payout">Mark as Paid</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
